import { getDocs, type DocumentData, type Query, type QuerySnapshot } from 'firebase/firestore';

/**
 * Thrown when a query is answered from an empty local cache because the client
 * is offline. Mirrors the `unavailable` FirestoreError the SDK itself throws
 * for an offline getDoc() — same code, same message — so the existing
 * isNetworkError checks, the SiteRepository retry and the Sentry ignoreErrors
 * rule all treat it as a connectivity problem. (FirestoreError's constructor is
 * private in the SDK typings, hence a sibling class rather than an instance.)
 */
export class FirestoreOfflineError extends Error {
  readonly code = 'unavailable' as const;

  constructor() {
    super('Failed to get document because the client is offline.');
    this.name = 'FirestoreOfflineError';
  }
}

/**
 * getDocs() does not reject when the client is offline. Once the SDK flips to
 * its offline state — a single failed Listen stream, or 10s without connecting
 * — it resolves the pending read from the local cache, and with the default
 * in-memory cache that is an *empty* snapshot flagged `fromCache`. Every
 * repository treats "empty" as "not found", so a visitor in a dead spot got
 * "Site not found for slug: muza" instead of the connection screen (Sentry
 * CUSTOM-APP-F). Reject that case instead. A non-empty from-cache snapshot is
 * data loaded earlier in this session and is returned as-is.
 */
export async function getDocsOnline<AppModelType, DbModelType extends DocumentData>(
  q: Query<AppModelType, DbModelType>
): Promise<QuerySnapshot<AppModelType, DbModelType>> {
  const snapshot = await getDocs(q);
  if (snapshot.empty && snapshot.metadata.fromCache) throw new FirestoreOfflineError();
  return snapshot;
}
